import React, { useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { deleteAccount } from "../actions/editProfile";
import { logout } from "../actions/logout";
import { toastAlert } from "../lib/toastAlert";

const CloseAccountModal = ({ show, onHide }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setErrors({});
    setSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const validate = () => {
    const next = {};
    if (!password.trim()) {
      next.password = "Enter your password to continue.";
    }
    if (!confirmPassword.trim()) {
      next.confirmPassword = "Confirm your password.";
    } else if (password !== confirmPassword) {
      next.confirmPassword = "Passwords do not match.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      setErrors({});
      const { status, message } = await deleteAccount({ password });
      if (status === true) {
        toastAlert("success", message, "profile");
        handleClose();
        logout(history, dispatch);
      } else {
        const msg = message || "Could not close account.";
        if (msg.toLowerCase().includes("password")) {
          setErrors({ password: msg });
        } else {
          toastAlert("error", msg, "profile");
        }
      }
    } catch (err) {
      console.log("close_account_err", err);
      toastAlert("error", "Could not close account.", "profile");
    } finally {
      setSubmitting(false);
    }
  };

  const passwordInputType = showPassword ? "text" : "password";
  const confirmInputType = showConfirmPassword ? "text" : "password";

  return (
    <Modal
      show={show}
      className="cstm_mdls close_account_modal"
      onHide={handleClose}
      aria-labelledby="close-account-modal"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Close account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="close_account_modal__notice">
          <p className="close_account_modal__desc mb-0">
            This permanently closes your account. You will be signed out and
            must contact support to reopen it.
          </p>
        </div>
        <p className="close_account_modal__hint mb-3">
          Enter your password below to confirm.
        </p>
        <Form className="custom_form" onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-3" controlId="closeAccountModalPassword">
            <Form.Label className="custom_label">Password</Form.Label>
            <Row className="mx-auto align-items-center">
              <Col xs={10} sm={11} className="px-0">
                <Form.Control
                  type={passwordInputType}
                  className="custom_input"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  disabled={submitting}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                />
              </Col>
              <Col
                xs={2}
                sm={1}
                className="d-flex align-items-center justify-content-end ps-0"
              >
                <button
                  type="button"
                  className="close_account_modal__toggle border-0 bg-transparent p-0"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </Col>
            </Row>
            {errors.password && (
              <p className="error-message text-danger mt-2 mb-0">
                {errors.password}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mb-0" controlId="closeAccountModalConfirm">
            <Form.Label className="custom_label">Confirm password</Form.Label>
            <Row className="mx-auto align-items-center">
              <Col xs={10} sm={11} className="px-0">
                <Form.Control
                  type={confirmInputType}
                  className="custom_input"
                  autoComplete="current-password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  disabled={submitting}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                  }}
                />
              </Col>
              <Col
                xs={2}
                sm={1}
                className="d-flex align-items-center justify-content-end ps-0"
              >
                <button
                  type="button"
                  className="close_account_modal__toggle border-0 bg-transparent p-0"
                  tabIndex={-1}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  onClick={() => setShowConfirmPassword((v) => !v)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </Col>
            </Row>
            {errors.confirmPassword && (
              <p className="error-message text-danger mt-2 mb-0">
                {errors.confirmPassword}
              </p>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column-reverse flex-sm-row">
        <button
          type="button"
          className="img_btn_invert"
          disabled={submitting}
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn img_btn close_account_modal__btn_confirm"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Closing…" : "Close my account"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseAccountModal;
